/**
 * Tells a store action whether its request is still the newest of its kind, so
 * a slow response can't overwrite the result of a request made after it.
 *
 *   const sequence = requestSequence();
 *
 *   // Resolves to the response, or null once a newer fetchOrders() started;
 *   // a superseded failure resolves to null too instead of throwing.
 *   async fetchOrders(params) {
 *     return sequence.run("orders", () => axios.get(ORDERS, { params }));
 *   }
 *
 *   // Or check by hand, e.g. to still throw a superseded failure:
 *   const isNewest = sequence.begin(`sellerType:${userId}`);
 *   const { data } = await axios.post(...);
 *   return isNewest() ? data.user : null;
 *
 * Keys are free-form: one per kind of request, or per kind and record.
 */
export function requestSequence() {
  const newest = new Map();

  function begin(key) {
    const seq = (newest.get(key) ?? 0) + 1;
    newest.set(key, seq);

    return () => newest.get(key) === seq;
  }

  async function run(key, request) {
    const isNewest = begin(key);

    try {
      const result = await request();
      return isNewest() ? result : null;
    } catch (error) {
      if (!isNewest()) return null;
      throw error;
    }
  }

  return { begin, run };
}
